import { router, useLocalSearchParams } from 'expo-router';
import { useEffect, useState } from 'react';
import { SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import { StyleSheet, useWindowDimensions, View } from 'react-native';
import { FlatList } from 'react-native-gesture-handler';
import { Divider } from 'react-native-paper';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useCreateManyPastors } from '@/api/hooks/pastorsHooks';
import { useUpdateNonProfitOrgWiki } from '@/api/hooks/nonProfitOrgHooks';
import AddAnotherButton from '@/components/buttons/AddAnotherButton';
import BrandedButton from '@/components/buttons/BrandedButton';
import PastorForm from '@/components/forms/PastorForm';
import FormWrapper from '@/components/forms/FormWrapper';
import FieldArrayListItemCard from '@/components/npo/onboarding/FieldArrayListItemCard';
import OnboardingSection from '@/components/npo/onboarding/OnboardingSection';
import Text from '@/components/Text';
import { useAuth } from '@/contexts/authContext';
import { useEndFlowModal } from '@/contexts/endFlowModalContext';
import { PastorsData, pastorsResolver, PastorData } from '@/lib/validations/NpoOnboardingSchema';
import { i18n } from '@/utils/i18n';
import { safeJsonParse } from '@/utils/jsonParse';
import { objectComparision } from '@/utils/objectComparision';
import { useWikiChangesNpoModal } from '@/contexts/wikiChangesNpoModalContext';

// Just for CHURCH type
const NpoPastors = () => {
  const { initialList, pastorDataJson, pastorIndex, npoId } = useLocalSearchParams<{
    initialList: string;
    pastorDataJson: string;
    pastorIndex: string;
    npoId: string;
  }>();

  const [npoIdState, setNpoIdState] = useState(npoId);

  const { state: authState } = useAuth();
  const { mutateAsync: createManyPastors, isPending: pastorsPending } = useCreateManyPastors();
  const { mutateAsync: updateNpoWiki, isPending: updateNpoPending } = useUpdateNonProfitOrgWiki();

  const { showSuccess, showError } = useEndFlowModal();
  const { setVisible, setNpoId } = useWikiChangesNpoModal();

  const { height } = useWindowDimensions();
  const { top, bottom } = useSafeAreaInsets();

  useEffect(() => {
    if (npoId) setNpoIdState(npoId);
  }, [npoId]);

  useEffect(() => {
    if (pastorDataJson) {
      const pastorData = safeJsonParse<PastorData>(pastorDataJson);
      if (pastorData && fields !== undefined) {
        // eslint-disable-next-line no-unused-expressions
        !pastorIndex && !fields.find((field) => objectComparision(field, pastorData))
          ? append(pastorData)
          : update(+pastorIndex, pastorData);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pastorDataJson]);

  const isIndividual = authState.authReady && authState.user?.user.role === 'INDIVIDUAL';

  const {
    control,
    handleSubmit,
    trigger,
    clearErrors,
    getValues,
    formState: { isValid, submitCount, errors }
  } = useForm<PastorsData>({
    defaultValues: { pastors: initialList !== undefined ? safeJsonParse<PastorData[]>(initialList) : [] },
    mode: 'onSubmit',
    resolver: pastorsResolver,
    context: { isIndividual }
  });

  const { fields, insert, append, update, remove } = useFieldArray({
    control,
    name: 'pastors'
  });

  const onSubmit: SubmitHandler<PastorsData> = async (data) => {
    const mappedData = data.pastors.map((pastor) => ({
      ...pastor,
      nonProfitOrgId: npoIdState ?? authState.signUpData?.loginEmail?.npoId ?? ''
    }));
    const body = {
      pastors: mappedData,
      npoId: npoIdState ?? authState.signUpData?.loginEmail?.npoId
    };

    try {
      if (authState.authReady && authState.user?.user.role === 'INDIVIDUAL') {
        updateNpoWiki(
          {
            npoId: npoIdState ?? '',
            pastors: body.pastors
          },
          {
            onSuccess: () => {
              setNpoId(npoIdState ?? '');
              setVisible(true);
            },
            onError: (e) => {
              showError({ message: e.message });
              console.error('Error', e);
            }
          }
        );
      } else {
        createManyPastors(body, {
          onSuccess: () => {
            showSuccess({
              title: i18n.t('end_flow.success_title'),
              message: i18n.t('npo_details.update_message')
            });
            router.back();
          },
          onError: (e) => {
            showError({ message: e.message });
            console.error('Error', e);
          }
        });
      }
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <FormWrapper scrollEnable minHeightMinus={160}>
      {/* Height - Header */}
      <View style={styles.container}>
        <OnboardingSection
          label={i18n.t('sign_in.nonProfit.pastors.title')}
          description={i18n.t('sign_in.nonProfit.pastors.description')}>
          {getValues('pastors') && getValues('pastors').length === 0 ? (
            // height - header - footer
            <View style={{ height: height - 160 - bottom - top }}>
              <PastorForm
                initialData={getValues('pastors.0')}
                showDoneButton
                showBackButton={false}
                isLoading={pastorsPending}
                manualValidation={true}
                onDone={(data, triggerPastor) => {
                  if (data.fullName && data.position) {
                    clearErrors();
                    insert(0, data);
                    handleSubmit(onSubmit)();
                  } else if (!data.fullName || !data.position || data.allowance || data.annualSalary) {
                    triggerPastor();
                  }
                }}
                onAdd={(data) => {
                  clearErrors();
                  update(0, data);
                  trigger('pastors.0').then((isValid) => {
                    if (isValid) {
                      router.navigate('/edit/addUpdatePastor');
                    }
                  });
                }}
                doneLabel={i18n.t('mix.done')}
              />
            </View>
          ) : (
            <View style={styles.flexOne}>
              <Divider style={styles.divider} />
              <FlatList
                style={styles.flexOne}
                contentContainerStyle={styles.flexOne}
                scrollEnabled={false}
                data={fields}
                keyExtractor={(pastor) => pastor.id}
                renderItem={(field) => (
                  <FieldArrayListItemCard
                    onPress={() => {
                      router.navigate({
                        pathname: '/edit/addUpdatePastor',
                        params: {
                          pastorDataJson: JSON.stringify(field.item),
                          pastorIndex: field.index
                        }
                      });
                    }}
                    onDelete={() => {
                      remove(field.index);
                    }}
                    title={field.item.fullName}
                    subtitle={field.item.position ?? ''}
                    budget={field.item.annualSalary}
                  />
                )}
                ListFooterComponent={
                  <AddAnotherButton
                    onPress={() => router.navigate('/edit/addUpdatePastor')}
                    label={i18n.t('sign_in.nonProfit.pastors.add_another')}
                  />
                }
              />
            </View>
          )}
        </OnboardingSection>

        <View style={styles.buttonsContainer}>
          {errors.root && <Text style={styles.errorText}>{errors?.root?.message}</Text>}

          {getValues('pastors') && getValues('pastors').length > 0 && (
            <BrandedButton
              loading={pastorsPending || updateNpoPending}
              disabled={!isValid && submitCount > 0}
              onPress={handleSubmit(onSubmit)}>
              {i18n.t('mix.next')}
            </BrandedButton>
          )}
        </View>
      </View>
    </FormWrapper>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'space-between'
  },

  flexOne: {
    flex: 1
  },

  buttonsContainer: {
    flexDirection: 'column',
    width: '100%'
  },

  errorText: {
    color: 'red',
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 10
  },

  divider: {
    marginBottom: 15
  }
});

export default NpoPastors;
